import { useState } from 'react';
import useFilter from '../../../../../../../components/Crud/useFilter';
import usePath from '../../../../../../../components/Hooks/usePath';
import { useOrderValueVisibility } from '../../../../../../../hooks/useOrderValueVisibility';
import PATHS from '../../../../../../../routes/routes.const';
import { useFindClassificationTypes } from '../../../../../../../services/modules/classificationTypes/classificationTypes.queries';
import useOrderBookStore from '../../../../../../../stores/OrderBook/orderBook.store';
import { ClassificationTypeEntityEnum } from '../../../../../../classifications/classification.types';
import { useOrderContext } from '../../../../../contexts/Order.context';
import {
  OrderHeadersGroupByEnum,
  OrderProductsViewActionsEnum,
} from '../../../../../products/orderProducts.types';
import {
  OrderProductsViewConfig,
  OrderProductsViewSelection,
  UseOrderProductsViewParams,
} from '../orderProductsViewButton.types';
import {
  DEFAULT_VIEW_SELECTION,
  buildViewOptions,
  canSubGroupBy,
  getAppliedViewSelection,
  getSubGroupByOptions,
  hasGrouping,
  isSameViewSelection,
  parseOptionKey,
} from '../orderProductsViewButton.utils';

const CLASSIFICATION_TYPES_LIMIT = 100;

const useOrderProductsView = ({
  areOptionsEnabled,
}: UseOrderProductsViewParams): OrderProductsViewConfig => {
  const filter = useFilter();
  const { isPath } = usePath();
  const { shouldHideOrderValues, showOrderValueVisibilityButton } = useOrderValueVisibility();
  const toggleOrderValueVisibility = useOrderBookStore((state) => state.toggleOrderValueVisibility);
  const { changeOrderReviewAction } = useOrderContext();
  const [draftSelection, setDraftSelection] = useState<OrderProductsViewSelection | null>(null);

  const appliedSelection: OrderProductsViewSelection = {
    ...getAppliedViewSelection({
      groupBy: filter.initialValues.GROUP_BY,
      groupByInfo: filter.initialValues.GROUP_BY_INFO,
      sortBy: filter.initialValues.SORT_BY,
    }),
    shouldShowOrderTotals: !shouldHideOrderValues,
  };

  const isClassificationGroupingApplied = [
    appliedSelection.groupByKey,
    appliedSelection.subGroupByKey,
  ].some(
    (optionKey) =>
      parseOptionKey(optionKey).value === OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE,
  );

  const { data: classificationTypes } = useFindClassificationTypes(
    {
      limit: CLASSIFICATION_TYPES_LIMIT,
      useAsFilter: true,
      skip: 0,
      entity: ClassificationTypeEntityEnum.PRODUCT_COLOR,
    },
    { enabled: areOptionsEnabled || isClassificationGroupingApplied },
  );

  const { groupBy: groupByOptions, sortBy: sortByOptions } = buildViewOptions({
    isOrderEditPage: isPath(PATHS.main.orders.editForm.path),
    classificationTypes: classificationTypes?.data ?? [],
  });

  const selection = draftSelection ?? appliedSelection;
  const isSubGroupByDisabled = !canSubGroupBy(selection.groupByKey);
  const subGroupByKey = isSubGroupByDisabled
    ? DEFAULT_VIEW_SELECTION.subGroupByKey
    : selection.subGroupByKey;

  const changeGroupBy = (optionKey: string) => {
    const isSubGroupByStillValid = optionKey !== subGroupByKey;

    setDraftSelection({
      ...selection,
      groupByKey: optionKey,
      subGroupByKey: isSubGroupByStillValid ? subGroupByKey : DEFAULT_VIEW_SELECTION.subGroupByKey,
    });
  };

  const applyOrderTotalsVisibility = () => {
    if (selection.shouldShowOrderTotals === appliedSelection.shouldShowOrderTotals) {
      return;
    }

    toggleOrderValueVisibility();
  };

  const applySelection = () => {
    applyOrderTotalsVisibility();

    const groupByOption = parseOptionKey(selection.groupByKey);
    const subGroupByOption = parseOptionKey(subGroupByKey);
    const hasSubGroupBy = subGroupByOption.value !== OrderHeadersGroupByEnum.DEFAULT;

    filter.addFilters({
      SORT_BY: selection.sortByKey,
      GROUP_BY: [groupByOption.value, hasSubGroupBy ? subGroupByOption.value : ''].filter(Boolean),
      GROUP_BY_INFO: JSON.stringify({
        classificationTypeId: [
          groupByOption.classificationTypeId,
          hasSubGroupBy ? subGroupByOption.classificationTypeId : undefined,
        ].filter(Boolean),
      }),
    });

    setDraftSelection(null);
  };

  return {
    groupBy: {
      isVisible: true,
      options: groupByOptions,
      selectedKey: selection.groupByKey,
      isDisabled: false,
      onChange: changeGroupBy,
    },
    subGroupBy: {
      isVisible: !isSubGroupByDisabled,
      options: getSubGroupByOptions(groupByOptions, selection.groupByKey),
      selectedKey: subGroupByKey,
      isDisabled: false,
      onChange: (optionKey) => setDraftSelection({ ...selection, subGroupByKey: optionKey }),
    },
    sortBy: {
      isVisible: true,
      options: sortByOptions,
      selectedKey: selection.sortByKey,
      isDisabled: false,
      onChange: (optionKey) =>
        setDraftSelection({ ...selection, subGroupByKey, sortByKey: optionKey }),
    },
    sections: {
      isVisible: hasGrouping(selection.groupByKey),
      onCollapseAll: () => changeOrderReviewAction(OrderProductsViewActionsEnum.HIDE_ALL),
      onExpandAll: () => changeOrderReviewAction(OrderProductsViewActionsEnum.EXPAND_ALL),
    },
    orderTotals: {
      isVisible: showOrderValueVisibilityButton,
      isChecked: !selection.shouldShowOrderTotals,
      onChange: (isChecked) =>
        setDraftSelection({ ...selection, subGroupByKey, shouldShowOrderTotals: !isChecked }),
    },
    footer: {
      canApply: !isSameViewSelection({ ...selection, subGroupByKey }, appliedSelection),
      canRestore: !isSameViewSelection({ ...selection, subGroupByKey }, DEFAULT_VIEW_SELECTION),
      onApply: applySelection,
      onRestore: () => setDraftSelection(DEFAULT_VIEW_SELECTION),
    },
    onDiscardChanges: () => setDraftSelection(null),
  };
};

export default useOrderProductsView;
